import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  AlertCircle,
  CheckCircle2,
  ExternalLink,
  FileText,
  Loader2,
  Send,
  Sparkles,
} from 'lucide-react';
import {
  getProposalReadiness,
  getProposalReadinessLabel,
  type PaginatedResponse,
  type Proposal,
  type ProposalStatus,
  type ProposalType,
  type ProposalVisibility,
} from '@oneup/shared';
import { proposalsApi } from '@/lib/api';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Card } from '@/components/ui/card';
import {
  DetailFact,
  DetailFactGrid,
  DetailSectionHeading,
  DetailsActionBar,
  DetailsDrawer,
  DetailsDrawerBody,
  DetailsDrawerError,
  DetailsDrawerSkeleton,
} from '@/components/details-drawer';
import { OrganizationPicker } from '@/components/organization-picker';
import { pushRecent } from '@/lib/recents';
import { useUiStore } from '@/stores/ui';

interface ProposalDrawerProps {
  proposalId: string;
  onClose?: () => void;
}

const PROPOSALS_LIST_KEY = ['proposals', 'all'] as const;
const PROPOSAL_DETAIL_KEY = (id: string) => ['proposals', 'detail', id] as const;
const PROPOSAL_TYPES: ProposalType[] = [
  'Website Build',
  'Webmaster Package',
  'Local SEO',
  'AI Voice Agent',
  'Automation',
  'Marketing Retainer',
  'Technical Estimate',
  'Custom',
];
const STATUSES: ProposalStatus[] = ['Draft', 'Submitted', 'Approved', 'Rejected', 'Invoiced'];
const VISIBILITIES: ProposalVisibility[] = ['Draft', 'Admin Only', 'Client Visible', 'Archived'];

export function ProposalDrawer({ proposalId, onClose }: ProposalDrawerProps) {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const queryClient = useQueryClient();

  const detailQuery = useQuery({
    queryKey: PROPOSAL_DETAIL_KEY(proposalId),
    queryFn: () => proposalsApi.get(proposalId),
    initialData: () => {
      const list = queryClient.getQueryData<PaginatedResponse<Proposal>>(PROPOSALS_LIST_KEY);
      return list?.items.find((proposal) => proposal.id === proposalId);
    },
    initialDataUpdatedAt: () => queryClient.getQueryState(PROPOSALS_LIST_KEY)?.dataUpdatedAt ?? 0,
    staleTime: 10_000,
  });

  const handleClose = () => {
    if (onClose) {
      onClose();
      return;
    }
    const qs = searchParams.toString();
    navigate(qs ? `/proposals?${qs}` : '/proposals', { replace: true });
  };

  if (detailQuery.data) {
    return <ProposalDrawerBody proposal={detailQuery.data} onClose={handleClose} />;
  }

  return (
    <DetailsDrawer
      title="Proposal details"
      icon={FileText}
      onClose={handleClose}
      widthClassName="sm:max-w-[680px] md:max-w-[760px]"
    >
      {detailQuery.isError ? (
        <DetailsDrawerError error={detailQuery.error} fallback="Could not load proposal." />
      ) : (
        <DetailsDrawerSkeleton />
      )}
    </DetailsDrawer>
  );
}

function ProposalDrawerBody({ proposal, onClose }: { proposal: Proposal; onClose: () => void }) {
  const queryClient = useQueryClient();
  const setNotice = useUiStore((state) => state.setNotice);
  const setActiveDrawer = useUiStore((state) => state.setActiveDrawer);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(() => toDraft(proposal));
  const readiness = getProposalReadiness(proposal);
  const readinessLabel = getProposalReadinessLabel(proposal);

  useEffect(() => {
    setDraft(toDraft(proposal));
  }, [proposal]);

  useEffect(() => {
    setActiveDrawer({ type: 'proposal', id: proposal.id, name: proposal.proposalName });
    pushRecent({
      type: 'proposal',
      id: proposal.id,
      name: proposal.proposalName,
      secondary: proposal.status,
    });
    return () => setActiveDrawer(null);
  }, [proposal.id, proposal.proposalName, proposal.status, setActiveDrawer]);

  const patchMutation = useMutation({
    mutationFn: (patch: Partial<Proposal>) => proposalsApi.update(proposal.id, patch),
    onSuccess: (updated) => {
      queryClient.invalidateQueries({ queryKey: ['proposals'] });
      queryClient.setQueryData(PROPOSAL_DETAIL_KEY(updated.id), updated);
      setNotice('Proposal updated');
      setEditing(false);
    },
    onError: (error) =>
      setNotice(error instanceof Error ? error.message : 'Save failed', 'destructive'),
  });

  const save = () => patchMutation.mutate(toPatch(draft));

  const markSubmitted = () => {
    if (!window.confirm('Mark this proposal as Submitted?')) return;
    patchMutation.mutate({ status: 'Submitted' });
  };

  const setClientVisible = () => {
    if (!window.confirm('Make this proposal Client Visible?')) return;
    patchMutation.mutate({ portalVisibility: 'Client Visible' });
  };

  const generateDocPlaceholder = () => {
    // TODO: Wire this to the proposal document generation automation.
    setNotice('Generate Doc is not wired yet');
  };

  const sendPlaceholder = () => {
    // TODO: Wire this to the proposal send/review flow.
    setNotice('Send Proposal is not wired yet');
  };

  return (
    <DetailsDrawer
      title={proposal.proposalName}
      icon={FileText}
      onClose={onClose}
      widthClassName="sm:max-w-[680px] md:max-w-[760px]"
      description={
        <>
          {proposal.status && <Badge variant={statusVariant(proposal.status)}>{proposal.status}</Badge>}
          {proposal.portalVisibility && (
            <Badge variant={proposal.portalVisibility === 'Client Visible' ? 'info' : 'muted'}>
              {proposal.portalVisibility}
            </Badge>
          )}
          <Badge variant={readinessLabel === 'Needs Pricing' ? 'warning' : 'success'}>
            {readinessLabel}
          </Badge>
          <span>{readiness.score}% ready</span>
          {patchMutation.isPending && (
            <span className="inline-flex items-center gap-1">
              <Loader2 className="h-3 w-3 animate-spin" aria-hidden />
              saving
            </span>
          )}
          {patchMutation.isError && (
            <span className="inline-flex items-center gap-1 text-destructive">
              <AlertCircle className="h-3 w-3" aria-hidden />
              save failed
            </span>
          )}
        </>
      }
    >
      <DetailsDrawerBody>
        <DetailsActionBar>
          <Button size="sm" variant="outline" onClick={() => setEditing((value) => !value)}>
            {editing ? 'Cancel' : 'Edit'}
          </Button>
          {editing && (
            <Button size="sm" onClick={save} disabled={patchMutation.isPending}>
              Save
            </Button>
          )}
          <Button size="sm" variant="outline" onClick={markSubmitted} disabled={patchMutation.isPending}>
            <CheckCircle2 className="h-3.5 w-3.5" aria-hidden />
            Mark Ready / Submitted
          </Button>
          <Button size="sm" variant="outline" onClick={setClientVisible} disabled={patchMutation.isPending}>
            Set Client Visible
          </Button>
          <Button size="sm" variant="outline" onClick={generateDocPlaceholder}>
            <Sparkles className="h-3.5 w-3.5" aria-hidden />
            Generate Doc
          </Button>
          <Button size="sm" variant="outline" onClick={sendPlaceholder}>
            <Send className="h-3.5 w-3.5" aria-hidden />
            Send Proposal
          </Button>
          {proposal.generatedPdfUrl && (
            <Button size="sm" variant="outline" asChild>
              <a href={proposal.generatedPdfUrl} target="_blank" rel="noreferrer">
                <ExternalLink className="h-3.5 w-3.5" aria-hidden />
                Open PDF
              </a>
            </Button>
          )}
          {proposal.generatedDocUrl && (
            <Button size="sm" variant="outline" asChild>
              <a href={proposal.generatedDocUrl} target="_blank" rel="noreferrer">
                <ExternalLink className="h-3.5 w-3.5" aria-hidden />
                Open Doc
              </a>
            </Button>
          )}
        </DetailsActionBar>

        {editing ? (
          <EditForm draft={draft} setDraft={setDraft} />
        ) : (
          <ReadOnlyProposal proposal={proposal} readiness={readiness} />
        )}
      </DetailsDrawerBody>
    </DetailsDrawer>
  );
}

function ReadOnlyProposal({
  proposal,
  readiness,
}: {
  proposal: Proposal;
  readiness: ReturnType<typeof getProposalReadiness>;
}) {
  return (
    <div className="space-y-4">
      <DetailFactGrid className="grid-cols-2 md:grid-cols-4">
        <DetailFact label="Amount" value={formatCurrency(proposal.proposalAmount)} />
        <DetailFact label="Deposit" value={formatCurrency(proposal.depositAmount)} />
        <DetailFact label="Proposal Date" value={formatDate(proposal.proposalDate)} />
        <DetailFact label="Valid Until" value={formatDate(proposal.validUntil)} />
        <DetailFact label="Type" value={proposal.proposalType} />
        <DetailFact label="Timeline" value={proposal.timeline} />
        <DetailFact
          label="Organization"
          value={proposal.organizationLabel ?? proposal.organizationIds?.[0]}
        />
        <DetailFact label="Lead" value={proposal.leadLabel ?? proposal.leadIds?.[0]} />
      </DetailFactGrid>

      {(readiness.missing.length > 0 || readiness.warnings.length > 0) && (
        <Card className="space-y-2 p-3">
          <DetailSectionHeading>Readiness</DetailSectionHeading>
          {readiness.missing.length > 0 && (
            <div>
              <div className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                Missing
              </div>
              <div className="mt-1 flex flex-wrap gap-1">
                {readiness.missing.map((item) => (
                  <Badge key={item} variant="warning">
                    {item}
                  </Badge>
                ))}
              </div>
            </div>
          )}
          {readiness.warnings.length > 0 && (
            <div>
              <div className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                Warnings
              </div>
              <div className="mt-1 flex flex-wrap gap-1">
                {readiness.warnings.map((item) => (
                  <Badge key={item} variant="muted">
                    {item}
                  </Badge>
                ))}
              </div>
            </div>
          )}
        </Card>
      )}

      <Section label="Scope Summary" value={proposal.scopeSummary} />
      <Section label="Deliverables" value={proposal.deliverables} />
      <Section label="Services/Items Description" value={proposal.servicesDescription} />
      <Section label="Payment Terms" value={proposal.paymentTerms} />
      <Section label="Client-Facing Next Step" value={proposal.clientFacingNextStep} />
      <Section label="Internal Next Action" value={proposal.internalNextAction} />
      <Section label="Notes" value={proposal.notes} />
      <Section label="Generated Doc URL" value={proposal.generatedDocUrl} link />
      <Section label="Generated PDF URL" value={proposal.generatedPdfUrl} link />

      <Card className="space-y-2 p-3">
        <DetailSectionHeading>Proposal Document</DetailSectionHeading>
        {proposal.proposalDocument?.length ? (
          <div className="space-y-1">
            {proposal.proposalDocument.map((attachment) => (
              <a
                key={attachment.id ?? attachment.url}
                href={attachment.url}
                target="_blank"
                rel="noreferrer"
                className="flex items-center gap-1.5 text-primary hover:underline"
              >
                <ExternalLink className="h-3.5 w-3.5" aria-hidden />
                {attachment.filename ?? attachment.url}
              </a>
            ))}
          </div>
        ) : (
          <p className="text-muted-foreground">No attachments</p>
        )}
      </Card>
    </div>
  );
}

function EditForm({
  draft,
  setDraft,
}: {
  draft: ProposalDraft;
  setDraft: React.Dispatch<React.SetStateAction<ProposalDraft>>;
}) {
  return (
    <Card className="space-y-3 p-3">
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <FormField label="Proposal Name" className="sm:col-span-2">
          <Input
            value={draft.proposalName}
            onChange={(event) => setDraft((d) => ({ ...d, proposalName: event.target.value }))}
          />
        </FormField>
        <FormField label="Proposal Type">
          <Select
            value={draft.proposalType || undefined}
            onValueChange={(proposalType) =>
              setDraft((d) => ({ ...d, proposalType: proposalType as ProposalType }))
            }
          >
            <SelectTrigger className="h-9">
              <SelectValue placeholder="Select type" />
            </SelectTrigger>
            <SelectContent>
              {PROPOSAL_TYPES.map((type) => (
                <SelectItem key={type} value={type}>
                  {type}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </FormField>
        <FormField label="Status">
          <Select
            value={draft.status || undefined}
            onValueChange={(status) =>
              setDraft((d) => ({ ...d, status: status as ProposalStatus }))
            }
          >
            <SelectTrigger className="h-9">
              <SelectValue placeholder="Select status" />
            </SelectTrigger>
            <SelectContent>
              {STATUSES.map((status) => (
                <SelectItem key={status} value={status}>
                  {status}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </FormField>
        <FormField label="Portal Visibility">
          <Select
            value={draft.portalVisibility || undefined}
            onValueChange={(portalVisibility) =>
              setDraft((d) => ({
                ...d,
                portalVisibility: portalVisibility as ProposalVisibility,
              }))
            }
          >
            <SelectTrigger className="h-9">
              <SelectValue placeholder="Select visibility" />
            </SelectTrigger>
            <SelectContent>
              {VISIBILITIES.map((visibility) => (
                <SelectItem key={visibility} value={visibility}>
                  {visibility}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </FormField>
        <FormField label="Proposal Amount">
          <Input
            type="number"
            min="0"
            step="0.01"
            value={draft.proposalAmount}
            onChange={(event) => setDraft((d) => ({ ...d, proposalAmount: event.target.value }))}
          />
        </FormField>
        <FormField label="Deposit Amount">
          <Input
            type="number"
            min="0"
            step="0.01"
            value={draft.depositAmount}
            onChange={(event) => setDraft((d) => ({ ...d, depositAmount: event.target.value }))}
          />
        </FormField>
        <FormField label="Proposal Date">
          <Input
            type="date"
            value={draft.proposalDate}
            onChange={(event) => setDraft((d) => ({ ...d, proposalDate: event.target.value }))}
          />
        </FormField>
        <FormField label="Valid Until">
          <Input
            type="date"
            value={draft.validUntil}
            onChange={(event) => setDraft((d) => ({ ...d, validUntil: event.target.value }))}
          />
        </FormField>
        <FormField label="Timeline" className="sm:col-span-2">
          <Input
            value={draft.timeline}
            onChange={(event) => setDraft((d) => ({ ...d, timeline: event.target.value }))}
          />
        </FormField>
        <FormField label="Organization">
          <OrganizationPicker
            value={draft.organizationId}
            valueLabel={draft.organizationName}
            onChange={(organizationId, organizationName) =>
              setDraft((d) => ({ ...d, organizationId, organizationName }))
            }
            onClear={() => setDraft((d) => ({ ...d, organizationId: '', organizationName: '' }))}
          />
        </FormField>
        <FormField label="Lead Record ID">
          <Input
            value={draft.leadId}
            onChange={(event) => setDraft((d) => ({ ...d, leadId: event.target.value }))}
          />
        </FormField>
        <FormField label="Scope Summary" className="sm:col-span-2">
          <TextArea
            value={draft.scopeSummary}
            onChange={(scopeSummary) => setDraft((d) => ({ ...d, scopeSummary }))}
          />
        </FormField>
        <FormField label="Deliverables" className="sm:col-span-2">
          <TextArea
            value={draft.deliverables}
            onChange={(deliverables) => setDraft((d) => ({ ...d, deliverables }))}
          />
        </FormField>
        <FormField label="Services/Items Description" className="sm:col-span-2">
          <TextArea
            value={draft.servicesDescription}
            onChange={(servicesDescription) =>
              setDraft((d) => ({ ...d, servicesDescription }))
            }
          />
        </FormField>
        <FormField label="Payment Terms" className="sm:col-span-2">
          <TextArea
            value={draft.paymentTerms}
            onChange={(paymentTerms) => setDraft((d) => ({ ...d, paymentTerms }))}
          />
        </FormField>
        <FormField label="Client-Facing Next Step" className="sm:col-span-2">
          <TextArea
            value={draft.clientFacingNextStep}
            onChange={(clientFacingNextStep) =>
              setDraft((d) => ({ ...d, clientFacingNextStep }))
            }
          />
        </FormField>
        <FormField label="Internal Next Action" className="sm:col-span-2">
          <TextArea
            value={draft.internalNextAction}
            onChange={(internalNextAction) =>
              setDraft((d) => ({ ...d, internalNextAction }))
            }
          />
        </FormField>
        <FormField label="Notes" className="sm:col-span-2">
          <TextArea value={draft.notes} onChange={(notes) => setDraft((d) => ({ ...d, notes }))} />
        </FormField>
      </div>
    </Card>
  );
}

interface ProposalDraft {
  proposalName: string;
  proposalType: ProposalType | '';
  status: ProposalStatus | '';
  portalVisibility: ProposalVisibility | '';
  proposalAmount: string;
  depositAmount: string;
  proposalDate: string;
  validUntil: string;
  organizationId: string;
  organizationName: string;
  leadId: string;
  servicesDescription: string;
  scopeSummary: string;
  deliverables: string;
  timeline: string;
  paymentTerms: string;
  clientFacingNextStep: string;
  internalNextAction: string;
  notes: string;
}

function toDraft(proposal: Proposal): ProposalDraft {
  return {
    proposalName: proposal.proposalName,
    proposalType: proposal.proposalType ?? '',
    status: proposal.status ?? '',
    portalVisibility: proposal.portalVisibility ?? '',
    proposalAmount: proposal.proposalAmount?.toString() ?? '',
    depositAmount: proposal.depositAmount?.toString() ?? '',
    proposalDate: proposal.proposalDate ?? '',
    validUntil: proposal.validUntil ?? '',
    organizationId: proposal.organizationIds?.[0] ?? '',
    organizationName: proposal.organizationLabel ?? '',
    leadId: proposal.leadIds?.[0] ?? '',
    servicesDescription: proposal.servicesDescription ?? '',
    scopeSummary: proposal.scopeSummary ?? '',
    deliverables: proposal.deliverables ?? '',
    timeline: proposal.timeline ?? '',
    paymentTerms: proposal.paymentTerms ?? '',
    clientFacingNextStep: proposal.clientFacingNextStep ?? '',
    internalNextAction: proposal.internalNextAction ?? '',
    notes: proposal.notes ?? '',
  };
}

function toPatch(draft: ProposalDraft): Partial<Proposal> {
  return {
    proposalName: draft.proposalName.trim(),
    proposalType: draft.proposalType || undefined,
    status: draft.status || undefined,
    portalVisibility: draft.portalVisibility || undefined,
    proposalAmount: parseOptionalNumber(draft.proposalAmount),
    depositAmount: parseOptionalNumber(draft.depositAmount),
    proposalDate: draft.proposalDate || undefined,
    validUntil: draft.validUntil || undefined,
    organizationIds: draft.organizationId.trim() ? [draft.organizationId.trim()] : [],
    leadIds: draft.leadId.trim() ? [draft.leadId.trim()] : [],
    servicesDescription: draft.servicesDescription,
    scopeSummary: draft.scopeSummary,
    deliverables: draft.deliverables,
    timeline: draft.timeline,
    paymentTerms: draft.paymentTerms,
    clientFacingNextStep: draft.clientFacingNextStep,
    internalNextAction: draft.internalNextAction,
    notes: draft.notes,
  };
}

function FormField({
  label,
  children,
  className,
}: {
  label: string;
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <label className={`flex flex-col gap-1 ${className ?? ''}`}>
      <span className="text-[10px] font-semibold uppercase tracking-[0.6px] text-muted-foreground">
        {label}
      </span>
      {children}
    </label>
  );
}

function TextArea({
  value,
  onChange,
}: {
  value: string;
  onChange: (value: string) => void;
}) {
  return (
    <textarea
      value={value}
      onChange={(event) => onChange(event.target.value)}
      rows={4}
      className="flex w-full resize-y rounded-md border border-input bg-background px-2.5 py-1.5 text-[13px] ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
    />
  );
}

function Section({ label, value, link = false }: { label: string; value?: string; link?: boolean }) {
  if (!value) return null;
  return (
    <Card className="space-y-1.5 p-3">
      <DetailSectionHeading>{label}</DetailSectionHeading>
      {link ? (
        <a href={value} target="_blank" rel="noreferrer" className="text-primary hover:underline">
          {value}
        </a>
      ) : (
        <p className="whitespace-pre-wrap leading-relaxed text-foreground">{value}</p>
      )}
    </Card>
  );
}

function statusVariant(status: ProposalStatus): 'muted' | 'info' | 'success' | 'destructive' | 'warning' {
  if (status === 'Approved' || status === 'Invoiced') return 'success';
  if (status === 'Submitted') return 'info';
  if (status === 'Rejected') return 'destructive';
  return 'warning';
}

function formatCurrency(value: number | undefined): string | undefined {
  if (value === undefined) return undefined;
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(value);
}

function formatDate(value: string | undefined): string | undefined {
  if (!value) return undefined;
  return value.slice(0, 10);
}

function parseOptionalNumber(value: string): number | undefined {
  if (!value.trim()) return undefined;
  const n = Number(value);
  return Number.isFinite(n) ? n : undefined;
}
